import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import styled from "styled-components";
import { useMobileMediaQuery } from "hooks/useMobileMediaQuery";
import { Body2, H1, H2, H3 } from "melodies-source/Text";
import { FlexColumn } from "Routes/styled";
import { DateTime } from "luxon";
import { cityStateFromAddress } from "Utils";
import { SvgBack as BackIcon } from "melodies-source/Svgs/Back";
import { Stats } from "./Stats";
import { useArtistEventContext } from "contexts/ArtistEventContext";
import { StatsMobile } from "./StatsMobile";
import { DownloadCsv } from "../Components/DownloadCsv";
import {
  doc,
  DocumentReference,
  getDoc,
  getFirestore,
} from "firebase/firestore";
import { useDesktopMediaQuery } from "hooks/useDesktopMediaQuery";
import { EventConfigRoot } from "@max/common";
import { useDocumentData } from "react-firebase-hooks/firestore";
import { AdditionalGroupsAvatars } from "Components/AdditionalGroups";
import { Card } from "Components";
import { Loader } from "./Loader";

let Venue;
let Details;

export const Info: React.FC = () => {
  const [isMultiDayEvent, setIsMultiDayEvent] = useState(false);
  const { event, eventLoading, entriesLoading } = useArtistEventContext();
  const eventIsVersion2 = event?.version === 2;
  const isMobile = useMobileMediaQuery();
  const isDesktop = useDesktopMediaQuery(1001);

  const { artistId } = useParams<{ artistId: string }>();

  const [setFreshEvent] = useDocumentData(
    eventIsVersion2
      ? (doc(
          getFirestore(),
          `set_fresh_events/${event._id}`,
        ) as DocumentReference<EventConfigRoot>)
      : null,
  );

  useEffect(() => {
    if (event?._id) {
      const ref = doc(getFirestore(), `set_fresh_events_multiday/${event._id}`);

      getDoc(ref).then((doc) => {
        if (doc.exists()) {
          setIsMultiDayEvent(true);
        }
      });
    }
  }, [event?._id]);

  Venue = isMobile ? H2 : H1;
  Details = isMobile ? Body2 : H3;

  return (
    <Card isElevated>
      <ActionsContainer>
        <BackLink to={`/${artistId}/reporting/set-live`}>
          <BackIcon /> SET.Live Reports
        </BackLink>
        {eventIsVersion2 && isDesktop && (
          <DownloadCsv isMultiDayEvent={isMultiDayEvent} />
        )}
      </ActionsContainer>
      <MainContainer>
        <InfoContainer>
          <FlexColumn>
            {eventLoading ? (
              <Loader />
            ) : (
              <>
                <TitleContainer>
                  <Venue>{event.venue}</Venue>
                  {!!setFreshEvent?.additionalGroups?.length && (
                    <AdditionalGroupsAvatars
                      ids={setFreshEvent.additionalGroups}
                    />
                  )}
                </TitleContainer>
                <Details>
                  {`
              ${
                DateTime.fromMillis(event.startsAt.toMillis())
                  .toLocaleString(DateTime.DATETIME_FULL)
                  .split(" at ")[1]
              } • ${DateTime.fromMillis(
                    event.startsAt.toMillis(),
                  ).toLocaleString(DateTime.DATE_MED)}`}
                </Details>
                <Details>
                  {cityStateFromAddress(event.address || "") || "Unknown"}
                </Details>{" "}
              </>
            )}
          </FlexColumn>
          {eventIsVersion2 && !isDesktop && (
            <StyledDownloadCsv isMultiDayEvent={isMultiDayEvent} />
          )}
        </InfoContainer>
        {(entriesLoading || eventIsVersion2) && (
          <StatsContainer>
            {!isDesktop ? <StatsMobile /> : <Stats />}
          </StatsContainer>
        )}
      </MainContainer>
    </Card>
  );
};

const StatsContainer = styled.div`
  @media (max-width: 1000px) {
    width: 100%;
  }
`;

const InfoContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  flex-direction: column;

  h1,
  h2 {
    color: var(--header-text-color);
    margin-bottom: 5px;
  }

  h1 {
    line-height: 1.2;
    margin-bottom: 5px;
  }

  h2 {
    line-height: 1;
  }

  h3 {
    color: var(--text-color);
  }

  ${({ theme }) => theme.media.maxTablet} {
    width: 100%;
  }

  ${({ theme }) => theme.media.mobile} {
    & > button {
      margin-top: 12px;
      width: 100%;
      max-width: 100%;
      justify-content: center;
      gap: 10px;
    }
  }
`;

const MainContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 30px;

  @media all and (max-width: 1000px) {
    flex-direction: column;
    margin-top: 25px;
  }
`;

const ActionsContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;

  ${({ theme }) => theme.media.desktop} {
    height: 44px;
  }
`;

const BackLink = styled(Link)`
  display: flex;
  justify-content: center;
  align-items: center;
  text-decoration: none;
  color: var(--main-color);
  font-weight: 500;
`;

const StyledDownloadCsv = styled(DownloadCsv)`
  @media (max-width: 1000px) {
    margin-top: 15px;
  }
`;

const TitleContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;

  ${(p) => p.theme.media.mobile} {
    flex-direction: column-reverse;
    align-items: flex-start;
    gap: 5px;
  }
`;
